/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Divider, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React from "react";
import { useSelector } from "react-redux";

import ImageWithPlaceholder, {
  PlaceholderImage,
} from "../../../../components/ImageWithPlaceholder";
import Modal from "../../../../components/Modal";
import Typography from "../../../../components/Typography";
import apiClient from "../../../../services/api-client";
import { generateImageUrl } from "../../../../util/helpers";
import { useBranch } from "../../../../util/useBranch";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import { ProductDetailsTabs } from "../../../products/components/ProductDetails/ProductDetailsTabs";
import { ProductDetailsApiResponse } from "../../../products/storage"; // Adjust the import path as necessary

interface ProductDetailModalProps {
  open: boolean;
  handleClose: () => void;
  productId: number | undefined;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  open,
  handleClose,
  productId,
}) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const branchId = useBranch();
  const { data: productDetails, isLoading } = useQuery<ProductDetailsApiResponse, AxiosError>({
    queryKey: ["productDetail", productId],
    queryFn: async () => {
      const response = await apiClient.get<ProductDetailsApiResponse, AxiosError>(
        `/products/${productId}?accountNumber=${currentAccount?.accountNumber}&branchId=${branchId}`
      );
      return response.data;
    },
    enabled: !!productId, // Only run the query if productId is defined
  });

  const renderDetails = () => {
    if (productDetails) {
      return (
        <Stack>
          <Stack gap={2} mb={4}>
            <Stack alignItems="center" height={260}>
              <ImageWithPlaceholder
                src={generateImageUrl(productDetails.itemNumber)}
                placeholderImageUrl={PlaceholderImage}
                size={500}
              />
            </Stack>
            <Stack gap={1}>
              <Typography variant="h3">{productDetails?.name}</Typography>
              <Typography variant="body2">{`SKU ${productDetails?.itemNumber}`}</Typography>
            </Stack>
            <Divider />
            <Typography variant="body2">{productDetails?.onlineShortDescription}</Typography>
          </Stack>
          <ProductDetailsTabs item={productDetails} />
        </Stack>
      );
    } else {
      return (
        <Stack height="100%" direction="column" alignItems="center" justifyContent="center">
          <Typography variant="caption" width="100%" textAlign="center">
            No product data found.
          </Typography>
        </Stack>
      );
    }
  };

  if (!productId) {
    return null;
  }

  return (
    <Modal
      title="Product Detail"
      open={open}
      handleClose={handleClose}
      maxWidth="md"
      paperProps={{
        sx: {
          height: {
            sm: "inherit",
            md: "844px",
          },
          overflow: "hidden",
        },
      }}
    >
      {isLoading ? <div>Loading...</div> : renderDetails()}
    </Modal>
  );
};

export const useProductDetailModalControl = () => {
  const [productDetailModalOpen, setProductDetailModalOpen] = React.useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = React.useState<number>();

  return {
    productId: selectedProductId,
    setOpen: (productId: number) => {
      setSelectedProductId(productId);
      setProductDetailModalOpen(true);
    },
    setClose: () => setProductDetailModalOpen(false),
    isOpen: productDetailModalOpen,
  };
};

export default ProductDetailModal;
